import { z } from 'zod';
import { isParcelTemplate } from './packaging.js';
import {
  ADDRESS_STATUSES,
  BANK_CALL_STATUSES,
  CLIENT_TYPES,
  COMM_CHANNELS,
  COMM_DIRECTIONS,
  CONTRACT_STATUSES,
  CONVERSATION_TYPES,
  COURIERS,
  CREDIT_TXN_TYPES,
  EXPENSE_CATEGORIES,
  FINANCE_CATEGORIES,
  FINANCE_DIRECTIONS,
  INCOME_CATEGORIES,
  LABEL_REQUEST_STATUSES,
  LEG_DIRECTIONS,
  MAIL_CATEGORIES,
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_CHANNELS,
  PRIORITIES,
  REPRESENTATIVE_STATUSES,
  REP_EXPENSE_CATEGORIES,
  REP_SERVICE_TYPES,
  REP_TICKET_CATEGORIES,
  ROLE_KEYS,
  SCAN_KINDS,
  SERVICE_TYPES,
  SHIPMENT_FLOWS,
  SHIPMENT_STATUSES,
  SHIPMENT_TYPES,
  TASK_CATEGORIES,
  TASK_STATUSES,
  TICKET_CATEGORIES,
  TICKET_MESSAGE_KINDS,
  TICKET_STATUSES,
  TRACKING_EVENT_TYPES,
} from './enums.js';
import {
  CLIENT_SERVICE_TYPES,
  CREDIT_BUREAUS,
  DOCUMENT_KEYS,
  EIN_STATUSES,
  ENROLLMENT_STATUSES,
  FOLLOW_UP_KINDS,
  FOLLOW_UP_STATUSES,
  GT_CALL_PURPOSES,
  GT_CALL_URGENCIES,
  BANK_CALL_PURPOSES,
  GT_OUTCOMES,
  ITIN_STATUSES,
  LLC_STATUSES,
  MILESTONE_STATUSES,
  BANK_KEYS,
  BANK_PRODUCT_STATUSES,
  BANK_PRODUCT_TYPES,
  BANK_RELATIONSHIP_STATUSES,
  OPS_EXPENSE_CATEGORIES,
  PHASE_STATUSES,
  RESPONSIBLE_PARTIES,
  STEP_STATUSES,
} from './services.js';

const id = z.string().uuid();
const optId = id.nullish();
const text = (max = 5000) => z.string().trim().max(max);
const reqText = (max = 500) => z.string().trim().min(1, 'Required').max(max);
const dateTime = z.coerce.date();
const optDateTime = z.coerce.date().nullish();
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const cents = z.number().int().min(0).max(100_000_000);

export const paginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  q: z.string().trim().max(200).optional(),
  sort: z.string().max(50).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
});

// ---------- Auth ----------
export const loginInput = z.object({ email: z.string().trim().toLowerCase().email(), password: z.string().min(1).max(200) });
export const passwordPolicy = z
  .string()
  .min(12, 'At least 12 characters')
  .max(200)
  .refine((p) => /[a-zA-Z]/.test(p) && /\d/.test(p), 'Use letters and numbers');
export const acceptInviteInput = z.object({ token: z.string().min(20).max(200), password: passwordPolicy });
export const changePasswordInput = z.object({ currentPassword: z.string().min(1).max(200), newPassword: passwordPolicy });

// ---------- Admin ----------
export const inviteUserInput = z.object({
  email: z.string().trim().toLowerCase().email(),
  fullName: reqText(120),
  title: text(120).optional(),
  phone: text(40).optional(),
  /** WhatsApp number for alerts (when empty, the mobile number is used for WhatsApp). */
  whatsapp: text(40).optional(),
  roles: z.array(z.enum(ROLE_KEYS)).min(1),
  departmentIds: z.array(id).default([]),
  supervisorId: optId,
});
/** A person's own (or a representative's) phone numbers: international format, or empty to clear. */
const contactNumber = z
  .string()
  .trim()
  // Pasted numbers often carry spaces, dashes, dots or invisible direction marks; keep only digits and +, and read a
  // leading 00 as +.
  .transform((v) => {
    const s = v.replace(/[^\d+]/g, '');
    return s.startsWith('00') ? `+${s.slice(2)}` : s;
  })
  .refine((v) => v === '' || /^\+[1-9]\d{6,14}$/.test(v), { message: 'Use international format, e.g. +15551234567' })
  .transform((v) => v || null)
  .nullish();
export const contactNumbersInput = z.object({ phone: contactNumber, whatsapp: contactNumber });
export const repContactInput = contactNumbersInput.extend({ email: z.string().trim().email().max(200).or(z.literal('')).nullish() });

export const updateUserInput = z.object({
  fullName: reqText(120).optional(),
  title: text(120).nullish(),
  phone: text(40).nullish(),
  whatsapp: text(40).nullish(),
  roles: z.array(z.enum(ROLE_KEYS)).min(1).optional(),
  departmentIds: z.array(id).optional(),
  supervisorId: optId,
});
export const departmentInput = z.object({ name: reqText(120), description: text(500).optional() });
export const rolePermissionsInput = z.object({ permissions: z.array(z.string().max(80)) });
export const businessHoursInput = z.object({
  timezone: z.string().min(3).max(64),
  workingDays: z.array(z.number().int().min(0).max(6)).min(1), // 0 = Sunday (Luxon weekday % 7)
  startTime: z.string().regex(/^\d{2}:\d{2}$/),
  endTime: z.string().regex(/^\d{2}:\d{2}$/),
});
export const holidayInput = z.object({ date: dateOnly, name: reqText(120) });
export const slaPolicyInput = z.object({
  priority: z.enum(PRIORITIES),
  responseMinutes: z.number().int().min(15).max(60 * 24 * 10),
  level1ResolutionMinutes: z.number().int().min(30).max(60 * 24 * 30),
  level2AckMinutes: z.number().int().min(15).max(60 * 24 * 10),
  level2ResolutionMinutes: z.number().int().min(30).max(60 * 24 * 30),
  level3ResolutionMinutes: z.number().int().min(30).max(60 * 24 * 60),
  maxPauseMinutes: z.number().int().min(0).max(60 * 24 * 30),
});
export const hubInput = z.object({
  name: reqText(100),
  contactName: text(100),
  phone: text(30),
  email: z.string().trim().email().max(200).or(z.literal('')),
  line1: reqText(200),
  line2: text(200),
  city: reqText(100),
  state: reqText(50),
  postalCode: reqText(20),
  countryCode: z.string().trim().regex(/^[A-Za-z]{2}$/).transform((v) => v.toUpperCase()),
});
export const settingsInput = z.object({
  routineTrackingNotifications: z.enum(['immediate', 'feed_only']).optional(),
  staleTrackingHours: z.number().int().min(6).max(24 * 14).optional(),
  clientEmailUpdates: z.boolean().optional(),
  /** Hours Finance has to pay an approved operational payment request (elapsed time from approval). */
  opsPaymentSlaHours: z.number().int().min(1).max(72).optional(),
  /** Queued emails older than this many hours are not sent (they expire), e.g. a backlog built up before email was set up. */
  staleEmailHours: z.number().int().min(1).max(24 * 30).optional(),
});

// ---------- Clients ----------
export const clientInput = z.object({
  fullName: reqText(200),
  email: z.string().trim().email().max(200).nullish().or(z.literal('')),
  phone: text(40).nullish(),
  whatsapp: text(40).nullish(),
  country: text(80).nullish(),
  // Delivery address, used as the destination of outbound labels.
  addressLine1: text(200).nullish(),
  addressLine2: text(200).nullish(),
  addressCity: text(100).nullish(),
  addressState: text(100).nullish(),
  addressPostalCode: text(20).nullish(),
  addressCountryCode: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, 'Use a 2-letter country code, e.g. NP or US')
    .transform((v) => v.toUpperCase())
    .nullish()
    .or(z.literal('')),
  notes: text().nullish(),
  clientType: z.enum(CLIENT_TYPES).default('b2c'),
  sellerId: optId,
  // The client allows our representative to open their mail and scan the contents (envelopes are always scanned).
  authorizedToOpen: z.boolean().optional(),
  // Automatic shipment status emails to the client (on by default).
  emailUpdates: z.boolean().optional(),
  departmentId: optId,
  // Service(s) purchased: each becomes its own enrollment with its own roadmap (never merged).
  services: z.lazy(() => z.array(enrollmentInput).max(2)).optional(),
});

export const sellerInput = z.object({
  name: reqText(200),
  contactName: text(200).nullish(),
  email: z.string().trim().email().max(200).nullish().or(z.literal('')),
  phone: text(40).nullish(),
  notes: text(2000).nullish(),
});

// ---------- Representatives / addresses ----------
export const representativeInput = z.object({
  fullName: reqText(200),
  email: z.string().trim().email().max(200).nullish().or(z.literal('')),
  phone: text(40).nullish(),
  whatsapp: text(40).nullish(),
  status: z.enum(REPRESENTATIVE_STATUSES).default('active'),
  supportedServices: z.array(z.enum(SERVICE_TYPES)).default([]),
  agreedPricing: text(2000).nullish(),
  notes: text().nullish(),
  // Who referred this representative, and who monitors them (people's names, e.g. "Shrijal Shrestha").
  masterReferral: text(200).nullish(),
  monitor: text(200).nullish(),
  /** Residential / business address (printed on the representative's services agreement). */
  address: text(500).nullish(),
  departmentId: optId,
});

export const addressInput = z.object({
  line1: reqText(200),
  line2: text(200).nullish(),
  city: reqText(100),
  state: z.string().trim().length(2).toUpperCase(),
  zip: z.string().trim().regex(/^\d{5}(-\d{4})?$/, 'Use a 5 or 9 digit ZIP'),
  representativeId: optId,
  status: z.enum(ADDRESS_STATUSES).default('available'),
  maxCapacity: z.number().int().min(0).max(10000).default(1),
  supportedServices: z.array(z.enum(SERVICE_TYPES)).default([]),
  agreedPricing: text(2000).nullish(),
  notes: text().nullish(),
  // Shown to the representative of this address in their portal.
  instructions: text(2000).nullish(),
  departmentId: optId,
});
export const addressAssignmentInput = z.object({ clientId: id, notes: text(500).nullish() });

// ---------- Contracts ----------
export const contractInput = z.object({
  title: reqText(200),
  representativeId: id,
  addressId: optId,
  status: z.enum(CONTRACT_STATUSES).default('draft'),
  effectiveDate: dateOnly.nullish(),
  expirationDate: dateOnly.nullish(),
  terms: text(20000).nullish(),
  assignedToId: optId,
});
export const contractStatusInput = z.object({
  status: z.enum(CONTRACT_STATUSES),
  note: text(2000).optional(),
  // When marking executed, the signed document and signer names must be recorded.
  representativeSignedName: text(200).optional(),
  companySignedName: text(200).optional(),
  signedDate: dateOnly.optional(),
});

// ---------- Tasks ----------
export const taskInput = z.object({
  title: reqText(200),
  description: text().nullish(),
  category: z.enum(TASK_CATEGORIES).default('other'),
  priority: z.enum(PRIORITIES).default('normal'),
  assigneeId: id,
  dueAt: optDateTime,
  nextAction: text(500).nullish(),
  nextFollowUpAt: optDateTime,
  clientId: optId,
  shipmentId: optId,
  representativeId: optId,
  ticketId: optId,
  /** Also send this task to the linked representative's WhatsApp/SMS (e.g. a representative without a portal login). */
  notifyRepresentative: z.boolean().optional(),
});
export const taskUpdateInput = z.object({
  title: reqText(200).optional(),
  description: text().nullish(),
  category: z.enum(TASK_CATEGORIES).optional(),
  priority: z.enum(PRIORITIES).optional(),
  dueAt: optDateTime,
  nextAction: text(500).nullish(),
  nextFollowUpAt: optDateTime,
});
export const taskStatusInput = z.object({
  status: z.enum(TASK_STATUSES),
  note: text(2000).optional(),
  completionEvidence: text(2000).optional(),
});
export const reassignInput = z.object({ assigneeId: id, reason: text(1000).optional() });
export const commentInput = z.object({ body: reqText(10000) });

// ---------- Shipments ----------
export const legInput = z.object({
  direction: z.enum(LEG_DIRECTIONS),
  courier: z.enum(COURIERS).nullish(),
  trackingNumber: z.string().trim().max(60).nullish(),
  labelReference: text(200).nullish(),
  expectedDeliveryAt: optDateTime,
});

export const shipmentInput = z.object({
  clientId: id,
  representativeId: optId,
  addressId: optId,
  assigneeId: optId,
  shipmentType: z.enum(SHIPMENT_TYPES).default('mail'),
  flow: z.enum(SHIPMENT_FLOWS).default('via_hub'),
  origin: text(300).nullish(),
  destination: text(300).nullish(),
  description: text(2000).nullish(),
  expectedDeliveryAt: optDateTime,
  nextFollowUpAt: optDateTime,
  internalNotes: text().nullish(),
  departmentId: optId,
  legs: z.array(legInput).max(3).default([]),
});
export const shipmentUpdateInput = shipmentInput
  .omit({ clientId: true, legs: true, flow: true, departmentId: true })
  .partial()
  .extend({ issueDescription: text(4000).nullish(), proofOfDelivery: text(2000).nullish() });
export const shipmentStatusInput = z.object({ status: z.enum(SHIPMENT_STATUSES), note: text(2000).optional() });
export const shipmentCloseInput = z.object({ verificationNote: reqText(2000), proofOfDelivery: text(2000).optional() });
export const legUpdateInput = legInput.omit({ direction: true }).partial();
export const manualTrackingEventInput = z.object({
  eventType: z.enum(TRACKING_EVENT_TYPES),
  occurredAt: dateTime,
  description: reqText(500),
  location: text(200).nullish(),
  reason: reqText(1000), // why a manual correction is needed
});

// ---------- Shipping labels ----------
export const labelAddressInput = z.object({
  contactName: reqText(100),
  companyName: text(100).nullish(),
  phone: reqText(30),
  email: z.string().trim().email().max(200).nullish().or(z.literal('')),
  line1: reqText(200),
  line2: text(200).nullish(),
  city: reqText(100),
  state: text(100).nullish(),
  postalCode: text(20).nullish(),
  countryCode: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, 'Use a 2-letter country code')
    .transform((v) => v.toUpperCase()),
});

/**
 * The parcel of a label: either carrier packaging (`template`, a Shippo parcel template token from CARRIER_PACKAGES —
 * the carrier's own dimensions are used, so length / width / height are ignored) or your own packaging with length,
 * width and height. The weight is always required.
 */
export const labelParcelInput = z.object({
  template: z
    .string()
    .trim()
    .nullish()
    .refine((v) => !v || isParcelTemplate(v), { message: 'Unknown carrier packaging' }),
  weightKg: z.number().positive().max(70),
  lengthCm: z.number().positive().max(300).nullish(),
  widthCm: z.number().positive().max(300).nullish(),
  heightCm: z.number().positive().max(300).nullish(),
  description: reqText(200),
  quantity: z.number().int().min(1).max(100).default(1),
  declaredValueUsd: z.number().min(0).max(100000),
  category: z.string().trim().max(80).default('documents'),
  hsCode: z.string().trim().max(20).nullish(),
});

export const labelRatesInput = z.object({
  origin: labelAddressInput,
  destination: labelAddressInput,
  parcel: labelParcelInput,
});
export const labelPurchaseInput = labelRatesInput.extend({
  courierServiceId: reqText(100),
  expectedTotalCents: cents.optional(), // shown price; the purchase is refused if the provider's price rose (see PRICE_TOLERANCE in labels/routes.ts)
  /** One key per "Buy" confirmation: a repeated request (double click, retry) returns the label already bought instead of buying again. */
  idempotencyKey: z.string().trim().min(8).max(100).optional(),
});

/** Multipart form fields arrive as strings: blank means "not given". */
const blank = <T extends z.ZodTypeAny>(schema: T) => z.preprocess((v) => (v === '' || v === null ? undefined : v), schema);

/**
 * A label bought outside the portal, recorded by staff for a journey: carrier, service, tracking number, cost and
 * (as a multipart file) the label PDF. The cost is posted to the ledger and the tracking number set on the journey.
 */
export const manualLabelInput = z
  .object({
    courier: blank(z.enum(COURIERS).optional()),
    /** Carrier name as printed on the label, e.g. "USPS" (defaults to the courier). */
    courierName: blank(text(100).optional()),
    serviceName: blank(text(100).optional()),
    trackingNumber: blank(z.string().trim().max(60).optional()),
    totalChargeCents: blank(z.coerce.number().int().min(0).max(100_000_000).optional()),
    currency: blank(
      z
        .string()
        .trim()
        .regex(/^[A-Za-z]{3}$/, 'Use a 3-letter currency code')
        .transform((v) => v.toUpperCase())
        .default('USD'),
    ),
  })
  .refine((v) => !!v.courier || !!v.courierName, { message: 'Choose the courier', path: ['courier'] });

// ---------- Bank calls ----------
export const bankCallInput = z.object({
  clientId: id,
  institution: reqText(120),
  reason: reqText(1000),
  assigneeId: optId,
  scheduledAt: optDateTime,
  clientAuthorizationConfirmed: z.boolean().default(false),
  notes: text().nullish(),
});
export const bankCallUpdateInput = z.object({
  status: z.enum(BANK_CALL_STATUSES).optional(),
  outcome: text(4000).nullish(),
  referenceNumber: text(120).nullish(),
  documentsRequested: text(2000).nullish(),
  notes: text().nullish(),
  nextFollowUpAt: optDateTime,
  scheduledAt: optDateTime,
  clientAuthorizationConfirmed: z.boolean().optional(),
  /**
   * Amex Global Transfer calls: the application result recorded in the same save (written through the Global
   * Transfer update path, so its validation, audit and history apply once).
   */
  globalTransfer: z
    .object({
      outcome: z.enum(GT_OUTCOMES).optional(),
      creditLimitUsd: z.coerce.number().min(0, 'Cannot be negative').max(10_000_000).nullish(),
      approvedCardProduct: text(120).nullish(),
      decisionDate: dateOnly.nullish(),
    })
    .strict()
    .optional(),
  /** Global Transfer calls: also tick the roadmap's "Banker calls" step when the call is completed. */
  completeBankerStep: z.boolean().optional(),
  /**
   * Bank-application calls (Credit Building): the product and/or relationship result recorded in the same save
   * (written through the bank-applications update path, so its validation and audit apply once).
   */
  bankApplication: z
    .object({
      productId: id.nullish(),
      product: z
        .object({
          status: z.enum(BANK_PRODUCT_STATUSES).optional(),
          decisionDate: dateOnly.nullish().or(z.literal('')),
          limitAmountUsd: z.coerce.number().min(0, 'Cannot be negative').max(10_000_000).nullish(),
          securedAmountUsd: z.coerce.number().min(0, 'Cannot be negative').max(10_000_000).nullish(),
          referenceNumber: text(80).nullish(),
          nextFollowUpDate: dateOnly.nullish().or(z.literal('')),
        })
        .strict()
        .optional(),
      relationshipStatus: z.enum(BANK_RELATIONSHIP_STATUSES).optional(),
    })
    .strict()
    .optional(),
});

// ---------- Communications ----------
export const communicationInput = z.object({
  channel: z.enum(COMM_CHANNELS),
  direction: z.enum(COMM_DIRECTIONS).default('outbound'),
  occurredAt: dateTime.optional(),
  summary: reqText(10000),
  nextAction: text(500).nullish(),
  followUpAt: optDateTime,
  shipmentId: optId,
  ticketId: optId,
});
export const clientCommunicationInput = communicationInput.extend({ clientId: id });
export const representativeCommunicationInput = communicationInput.extend({ representativeId: id });

// ---------- Tickets ----------
export const ticketInput = z.object({
  title: reqText(200),
  description: reqText(10000),
  category: z.enum(TICKET_CATEGORIES),
  priority: z.enum(PRIORITIES).default('normal'),
  ownerId: optId,
  clientId: optId,
  shipmentId: optId,
  representativeId: optId,
  nextAction: text(500).nullish(),
});
export const ticketUpdateInput = z.object({
  title: reqText(200).optional(),
  priority: z.enum(PRIORITIES).optional(),
  category: z.enum(TICKET_CATEGORIES).optional(),
  nextAction: reqText(500).optional(),
  nextActionDueAt: optDateTime,
});
export const ticketStatusInput = z.object({
  status: z.enum(TICKET_STATUSES),
  note: text(2000).optional(),
  pauseReason: text(1000).optional(),
});
export const ticketMessageInput = z.object({
  kind: z.enum(TICKET_MESSAGE_KINDS).refine((k) => k !== 'system', 'System messages are generated automatically'),
  body: reqText(20000),
  channel: z.enum(COMM_CHANNELS).nullish(),
  counterpart: text(200).nullish(), // representative / client / courier name or handle
  direction: z.enum(COMM_DIRECTIONS).nullish(),
});
export const escalateInput = z.object({
  reason: reqText(4000),
  actionsAttempted: reqText(4000),
  currentIssue: reqText(4000),
  urgency: z.enum(PRIORITIES),
  recommendedAction: reqText(4000),
  // Level 2 -> 3 additionally requires these:
  coordinatorActions: text(4000).optional(),
  representativeResponse: text(4000).optional(),
});
export const resolveInput = z.object({
  summary: reqText(4000),
  actionsTaken: reqText(4000),
  outcome: reqText(2000),
  evidence: text(4000).optional(),
});
export const reopenInput = z.object({ reason: reqText(2000) });
export const managementDecisionInput = z.object({ decision: reqText(4000) });

// ---------- Payments ----------
export const paymentRequestInput = z.object({
  representativeId: id,
  shipmentId: optId,
  serviceType: z.enum(SERVICE_TYPES),
  agreedPriceCents: cents,
  additionalChargesCents: cents.default(0),
  additionalChargesNote: text(1000).nullish(),
  serviceCompletedOn: dateOnly,
  notes: text(4000).nullish(),
  submit: z.boolean().default(false),
});
export const paymentDecisionInput = z.object({ note: text(4000).optional() });
/** Jason's authorization, or (at or above the owner-approval threshold, after Jason) the owner's final approval with a step-up token. */
export const paymentAuthorizeInput = paymentDecisionInput.extend({ stepUpToken: z.string().trim().max(200).optional() });
export const paymentRequiredNoteInput = z.object({ note: reqText(4000) });
export const paymentProcessInput = z.object({
  method: reqText(120),
  confirmationReference: reqText(200),
  paidOn: dateOnly,
  bankAccount: text(120).nullish(), // company account the money was sent from
  note: text(2000).optional(),
});

// ---------- Company finance ----------
const money = z.number().positive().max(100_000_000);
export const financeEntryInput = z
  .object({
    entryDate: dateOnly,
    direction: z.enum(FINANCE_DIRECTIONS),
    category: z.enum(FINANCE_CATEGORIES),
    amountCents: z.number().int().positive().max(10_000_000_000).optional(),
    // Foreign-currency payments (e.g. salaries in NPR): original amount × exchange rate → USD.
    originalAmount: money.nullish(),
    originalCurrency: z.string().trim().regex(/^[A-Za-z]{3}$/).transform((v) => v.toUpperCase()).nullish(),
    exchangeRate: z.number().positive().max(1_000_000).nullish(), // USD per 1 unit of the original currency
    description: reqText(500),
    clientId: optId,
    sellerId: optId,
    payeeId: optId,
    payeeName: text(200).nullish(),
    period: z.string().regex(/^\d{4}-\d{2}$/).nullish(),
    method: text(60).nullish(),
    bankAccount: text(120).nullish(),
    reference: text(200).nullish(),
    paidAt: z.string().datetime({ offset: true }).nullish(),
    shipmentId: optId,
  })
  .refine((v) => v.amountCents || (v.originalAmount && v.exchangeRate), { message: 'Enter the USD amount, or the original amount and exchange rate', path: ['amountCents'] })
  .refine((v) => (v.direction === 'in' ? (INCOME_CATEGORIES as readonly string[]).includes(v.category) : (EXPENSE_CATEGORIES as readonly string[]).includes(v.category)), {
    message: 'This category does not match money in / money out',
    path: ['category'],
  });
export const financeVoidInput = z.object({ reason: reqText(500) });
export const payeeInput = z.object({
  name: reqText(200),
  kind: z.enum(['staff', 'representative', 'vendor']).default('staff'),
  userId: optId,
  representativeId: optId,
  defaultAmount: money.nullish(),
  defaultCurrency: z.string().trim().regex(/^[A-Za-z]{3}$/).transform((v) => v.toUpperCase()).default('USD'),
  payDetails: text(1000).nullish(),
  notes: text(2000).nullish(),
  active: z.boolean().default(true),
});
export const creditAccountInput = z.object({
  sellerId: id,
  startingBalanceCents: z.number().int().min(-10_000_000_000).max(10_000_000_000).default(0),
  lowBalanceCents: z.number().int().min(0).max(10_000_000_000).default(100000),
});
export const creditTxnInput = z.object({
  txnDate: dateOnly,
  type: z.enum(CREDIT_TXN_TYPES),
  amountCents: z.number().int().positive().max(10_000_000_000),
  note: text(500).nullish(),
  clientId: optId,
  // A credit received is money in: also record it in the ledger (default) with these payment details.
  recordAsIncome: z.boolean().default(true),
  method: text(60).nullish(),
  bankAccount: text(120).nullish(),
  reference: text(200).nullish(),
});
export const financeSettingsInput = z.object({
  companyName: reqText(200),
  dba: text(200).nullish(),
  address: text(300).nullish(),
  openingCashCents: z.number().int().min(-10_000_000_000).max(10_000_000_000).default(0),
  openingDate: dateOnly.nullish(), // ledger starts on this date with the opening cash
  preparedBy: text(120).nullish(),
  bankAccounts: z.array(z.string().trim().min(1).max(120)).max(30).default([]),
});

// ---------- Checklists & reports ----------
export const checklistItemUpdateInput = z.object({
  completed: z.boolean(),
  notes: text(2000).nullish(),
  evidence: text(2000).nullish(),
  linkedTaskId: optId,
});
export const reportNotesInput = z.object({ notes: text(10000) });

// ---------- Notifications ----------
export const notificationPreferenceInput = z.object({
  preferences: z.array(
    z.object({
      category: z.enum(NOTIFICATION_CATEGORIES),
      channel: z.enum(NOTIFICATION_CHANNELS),
      enabled: z.boolean(),
    }),
  ),
});

export type LoginInput = z.infer<typeof loginInput>;
export type InviteUserInput = z.infer<typeof inviteUserInput>;
export type ClientInput = z.infer<typeof clientInput>;
export type SellerInput = z.infer<typeof sellerInput>;
export type RepresentativeInput = z.infer<typeof representativeInput>;
export type AddressInput = z.infer<typeof addressInput>;
export type LabelAddress = z.infer<typeof labelAddressInput>;
export type LabelParcel = z.infer<typeof labelParcelInput>;
export type ContractInput = z.infer<typeof contractInput>;
export type TaskInput = z.infer<typeof taskInput>;
export type ShipmentInput = z.infer<typeof shipmentInput>;
export type BankCallInput = z.infer<typeof bankCallInput>;
export type TicketInput = z.infer<typeof ticketInput>;
export type EscalateInput = z.infer<typeof escalateInput>;
export type ResolveInput = z.infer<typeof resolveInput>;
export type PaymentRequestInput = z.infer<typeof paymentRequestInput>;

// ---------- Address representative portal ----------
export const mailItemInput = z.object({
  addressId: id,
  clientId: optId,
  recipientName: text(200).nullish(),
  category: z.enum(MAIL_CATEGORIES),
  sender: text(200).nullish(),
  receivedAt: z.string().datetime({ offset: true }).optional(),
  notes: text(2000).nullish(),
  // Idempotency: the phone generates this once per registration so a retried upload never creates a duplicate.
  clientRequestId: z.string().uuid().optional(),
});
export const scanCreateInput = z.object({
  kind: z.enum(SCAN_KINDS).default('envelope'),
  notes: text(2000).nullish(),
});
export const scanReviewInput = z.object({ decision: z.enum(['approve', 'request_correction']), note: text(2000).nullish() });
export const labelRequestInput = z.object({
  mailItemId: optId,
  shipmentId: optId,
  instructions: text(2000).nullish(),
  destination: text(500).nullish(),
});
export const labelRequestUpdateInput = z.object({
  status: z.enum(LABEL_REQUEST_STATUSES).optional(),
  assignedToId: optId,
  note: text(2000).nullish(),
  shipmentId: optId,
  legId: optId,
  courier: text(60).nullish(),
  trackingNumber: text(80).nullish(),
});
export const dropoffInput = z.object({
  labelRequestId: optId,
  shipmentId: optId,
  droppedOffAt: z.string().datetime({ offset: true }),
  courier: reqText(60),
  location: text(300).nullish(),
  notes: text(2000).nullish(),
  reimbursementCents: z.number().int().min(0).max(100_000).default(0), // postage the representative paid, with receipt
  clientRequestId: z.string().uuid().optional(),
});
export const dropoffReviewInput = z.object({
  decision: z.enum(['verify', 'request_correction', 'reject']),
  note: text(2000).nullish(),
  approvedReimbursementCents: z.number().int().min(0).max(100_000).optional(),
});
export const repExpenseInput = z
  .object({
    category: z.enum(REP_EXPENSE_CATEGORIES),
    amountCents: z.number().int().positive().max(100_000_00),
    expenseDate: dateOnly,
    description: text(500).nullish(),
    mileage: z.number().min(0).max(10_000).nullish(),
    mailItemId: optId,
    labelRequestId: optId,
    clientRequestId: z.string().uuid().optional(),
  })
  .refine((v) => v.category !== 'other' || !!v.description?.trim(), { message: 'Describe the expense', path: ['description'] });
export const repExpenseReviewInput = z
  .object({
    decision: z.enum(['approve', 'reject']),
    note: text(2000).nullish(),
    approvedAmountCents: z.number().int().positive().max(100_000_00).optional(),
  })
  .refine((v) => v.decision !== 'reject' || !!v.note?.trim(), { message: 'Give the reason', path: ['note'] });
/** stepUpToken: the owner's approval is final; at or above the owner threshold it needs his fresh authenticator code. */
export const settlementDecisionInput = z.object({ decision: z.enum(['approve', 'reject']), note: text(2000).nullish(), stepUpToken: z.string().trim().max(200).optional() });
/** Owner's final approval of a settlement at or above the owner-approval threshold (approve needs a fresh step-up token). */
export const settlementFinalInput = settlementDecisionInput.extend({ stepUpToken: z.string().trim().max(200).optional() });
export const settlementPayInput = z.object({
  method: reqText(60),
  reference: reqText(200),
  paidOn: dateOnly,
  bankAccount: text(120).nullish(),
  note: text(2000).nullish(),
});
export const repRateInput = z.object({
  representativeId: optId, // null = default rate for everyone
  serviceType: z.enum(REP_SERVICE_TYPES),
  amountCents: z.number().int().min(0).max(1_000_000),
});
export const conversationStartInput = z.object({
  type: z.enum(CONVERSATION_TYPES).default('direct'),
  subjectId: optId,
  representativeId: optId, // staff starting a conversation with a representative
  body: reqText(5000),
});
export const messageInput = z.object({ body: reqText(5000), clientRequestId: z.string().uuid().optional() });
export const repTicketInput = z.object({
  title: reqText(200),
  description: reqText(5000),
  category: z.enum(REP_TICKET_CATEGORIES),
  priority: z.enum(PRIORITIES).default('normal'),
  shipmentId: optId,
  mailItemId: optId,
});
export const closeoutInput = z.object({
  items: z.array(z.object({ key: z.string().max(40), done: z.boolean(), exception: text(1000).nullish() })).max(20),
  notes: text(2000).nullish(),
});
export const pushSubscriptionInput = z.object({
  endpoint: z.string().url().max(1000),
  keys: z.object({ p256dh: z.string().max(200), auth: z.string().max(100) }),
});
export const addressAssignInput = z.object({ representativeId: optId, note: text(500).nullish() });

// ---------- Client shipment updates ----------
export const shipmentQuoteInput = z.object({
  amountCents: z.number().int().positive().max(100_000_000),
  currency: z.string().trim().regex(/^[A-Za-z]{3}$/).transform((v) => v.toUpperCase()).default('USD'),
  courierName: text(120).nullish(),
  deliveryEstimate: text(120).nullish(),
  note: text(1000).nullish(),
  expiresInDays: z.number().int().min(1).max(60).default(7),
});
export const quoteResponseInput = z.object({ note: text(1000).nullish() });

/** "Track a package": one shipment per tracking number (bought outside the portal). */
export const TRACK_JOURNEYS = ['to_hub', 'to_client', 'to_representative'] as const;
export const trackPackageInput = z.object({
  trackingNumbers: z.array(z.string().trim().min(1).max(60)).min(1).max(50),
  courier: z.enum(COURIERS).nullish(),
  clientId: id,
  journey: z.enum(TRACK_JOURNEYS).default('to_hub'),
  representativeId: optId,
  assigneeId: optId,
  shipmentType: z.enum(SHIPMENT_TYPES).default('mail'),
  description: text(2000).nullish(),
});

// ---------- Client services (Credit Building / Global Transfer) ----------
export const enrollmentInput = z.object({
  serviceType: z.enum(CLIENT_SERVICE_TYPES),
  caseManagerId: optId,
  bankerId: optId,
  supervisorId: optId,
  includesLlc: z.boolean().default(false),
  includesEin: z.boolean().default(false),
  includesItin: z.boolean().default(false),
  /** Defaults to true for Credit Building; never used for Global Transfer. */
  includesIdPack: z.boolean().optional(),
  businessOrPersonal: z.enum(['personal', 'business']).nullish(),
  purchaseRef: text(200).nullish(),
  source: text(120).nullish(),
  startedOn: dateOnly.optional(),
  notes: text(5000).nullish(),
});
export type EnrollmentInput = z.infer<typeof enrollmentInput>;
export const enrollmentUpdateInput = enrollmentInput.omit({ serviceType: true, startedOn: true }).partial().extend({ status: z.enum(ENROLLMENT_STATUSES).optional() });

export const phaseUpdateInput = z.object({
  status: z.enum(PHASE_STATUSES).optional(),
  assigneeId: optId,
  startDate: dateOnly.nullish(),
  targetDate: dateOnly.nullish(),
  completedDate: dateOnly.nullish(),
  blocker: text(2000).nullish(),
  nextAction: text(1000).nullish(),
  followUpAt: optDateTime,
  clientVisibleStatus: text(300).nullish(),
  clientVisibleNote: text(2000).nullish(),
  estimatedWindow: text(120).nullish(),
});
export const stepUpdateInput = z.object({
  status: z.enum(STEP_STATUSES).optional(),
  assigneeId: optId,
  dueAt: optDateTime,
  note: text(2000).optional(),
});

const optDate = dateOnly.nullish();
export const llcUpdateInput = z.object({
  proposedName: text(200).nullish(),
  approvedName: text(200).nullish(),
  stateOfFormation: text(60).nullish(),
  entityType: text(60).nullish(),
  registeredAgent: text(200).nullish(),
  filingSubmittedOn: optDate,
  stateFilingRef: text(120).nullish(),
  formationApprovedOn: optDate,
  status: z.enum(LLC_STATUSES).optional(),
  einAppliedOn: optDate,
  einIssuedOn: optDate,
  einStatus: z.enum(EIN_STATUSES).optional(),
  /** Full EIN may be entered once; only the last four digits are stored. */
  ein: z.string().trim().regex(/^\d{2}-?\d{7}$/, 'Use the 9-digit EIN').optional(),
  einConfirmationAttachmentId: optId,
  operatingAgreementStatus: z.enum(MILESTONE_STATUSES).optional(),
  formationDocumentsStatus: z.enum(MILESTONE_STATUSES).optional(),
  dunsStatus: z.enum(MILESTONE_STATUSES).optional(),
  dunsNumber: z.string().trim().regex(/^\d{9}$/, 'D-U-N-S is 9 digits').nullish(),
  formationConfirmationAttachmentId: optId,
  responsibleId: optId,
  pendingActions: text(2000).nullish(),
  nextFollowUpAt: optDateTime,
});
export const itinUpdateInput = z.object({
  required: z.boolean().optional(),
  preparationStatus: z.enum(MILESTONE_STATUSES).optional(),
  w7Status: z.enum(MILESTONE_STATUSES).optional(),
  supportingDocsStatus: z.enum(MILESTONE_STATUSES).optional(),
  submittedOn: optDate,
  submissionMethod: z.enum(['mail', 'acceptance_agent', 'irs_office', 'other']).nullish(),
  irsAcknowledgment: text(500).nullish(),
  status: z.enum(ITIN_STATUSES).optional(),
  additionalInfoRequested: text(2000).nullish(),
  correctionRequested: text(2000).nullish(),
  issuedOn: optDate,
  completedOn: optDate,
  /** Full ITIN may be entered once; only the last four digits are stored. */
  itin: z.string().trim().regex(/^9\d{2}-?\d{2}-?\d{4}$/, 'ITINs are 9 digits starting with 9').optional(),
  confirmationAttachmentId: optId,
  responsibleId: optId,
  nextFollowUpAt: optDateTime,
});

export const documentRequestInput = z.object({
  docKey: z.enum(DOCUMENT_KEYS as [string, ...string[]]),
  enrollmentId: optId,
  note: text(1000).nullish(),
  clientVisible: z.boolean().default(true),
  dueAt: optDateTime,
});
export const documentReviewInput = z.object({ decision: z.enum(['approve', 'reject']), reason: text(1000).optional() }).refine((v) => v.decision === 'approve' || !!v.reason, { message: 'Give a reason', path: ['reason'] });

export const serviceUpdateInput = z.object({ title: reqText(200), body: text(4000).nullish(), visibleToClient: z.boolean().default(true), phaseId: optId });

export const followUpInput = z.object({
  kind: z.enum(FOLLOW_UP_KINDS),
  dueAt: dateTime,
  ownerId: optId,
  phaseId: optId,
  responsibleParty: z.enum(RESPONSIBLE_PARTIES).default('staff'),
  notes: text(2000).nullish(),
});
export const followUpUpdateInput = z.object({ status: z.enum(FOLLOW_UP_STATUSES).optional(), dueAt: optDateTime, ownerId: optId, responsibleParty: z.enum(RESPONSIBLE_PARTIES).optional(), notes: text(2000).nullish() });

// Amex Global Transfer application details. Never accepts full card numbers, SSN/ITIN numbers or passwords:
// unknown keys are rejected, the foreign Amex card is at most its last 4 digits and SSN/ITIN is yes/no only.
const usd = (max: number) => z.coerce.number().min(0, 'Cannot be negative').max(max);
export const globalTransferDetailsInput = z
  .object({
    legalName: text(200).nullish(),
    dateOfBirth: dateOnly.nullish(),
    email: z.string().trim().toLowerCase().email().max(200).nullish().or(z.literal('')),
    phone: text(40).nullish(),
    addressLine1: text(200).nullish(),
    addressLine2: text(200).nullish(),
    addressCity: text(120).nullish(),
    addressState: text(60).nullish(),
    addressZip: z.string().trim().regex(/^\d{5}(-\d{4})?$/, 'US ZIP code (12345 or 12345-6789)').nullish().or(z.literal('')),
    amexCardCountry: text(80).nullish(),
    amexCardProduct: text(120).nullish(),
    amexCardLast4: z.string().trim().regex(/^\d{4}$/, 'Only the last 4 digits of the card — never the full number').nullish().or(z.literal('')),
    cardMemberSince: z.string().trim().regex(/^\d{4}(-(0[1-9]|1[0-2]))?$/, 'Use YYYY or YYYY-MM').nullish().or(z.literal('')),
    annualIncomeUsd: usd(20_000_000).nullish(),
    employmentStatus: text(80).nullish(),
    occupation: text(120).nullish(),
    employer: text(160).nullish(),
    housingStatus: text(80).nullish(),
    monthlyHousingUsd: usd(1_000_000).nullish(),
    citizenshipCountry: text(80).nullish(),
    hasSsnOrItin: z.boolean().nullish(),
    applicationType: z.enum(['personal', 'business']).nullish(),
    desiredCardProduct: text(120).nullish(),
    notes: text(5000).nullish(),
    assigneeId: optId,
    outcome: z.enum(GT_OUTCOMES).optional(),
    creditLimitUsd: usd(10_000_000).nullish(),
    approvedCardProduct: text(120).nullish(),
    decisionDate: dateOnly.nullish(),
  })
  .strict();
export type GlobalTransferDetailsInput = z.infer<typeof globalTransferDetailsInput>;
/** Arju (or anyone working Global Transfer) asks the banker specialist to call the Amex rep about a client. */
export const gtAmexCallRequestInput = z
  .object({
    purpose: z.enum(GT_CALL_PURPOSES),
    purposeDetail: text(1000).nullish(),
    urgency: z.enum(GT_CALL_URGENCIES).default('normal'),
    preferredTime: text(200).nullish(),
    scheduledAt: optDateTime,
    notes: text(4000).nullish(),
    assigneeId: optId,
    clientAuthorizationConfirmed: z.boolean().default(false),
  })
  .strict()
  .refine((v) => v.purpose !== 'other' || !!v.purposeDetail?.trim(), { message: 'Describe what the call is about', path: ['purposeDetail'] });
export type GtAmexCallRequestInput = z.infer<typeof gtAmexCallRequestInput>;
/** Ask the banker-call specialist to call a bank about a client's bank application (Credit Building). */
export const bankCallRequestInput = z
  .object({
    productId: optId,
    purpose: z.enum(BANK_CALL_PURPOSES),
    purposeDetail: text(1000).nullish(),
    urgency: z.enum(GT_CALL_URGENCIES).default('normal'),
    preferredTime: text(200).nullish(),
    scheduledAt: optDateTime,
    notes: text(4000).nullish(),
    assigneeId: optId,
    clientAuthorizationConfirmed: z.boolean().default(false),
  })
  .strict()
  .refine((v) => v.purpose !== 'other' || !!v.purposeDetail?.trim(), { message: 'Describe what the call is about', path: ['purposeDetail'] });
export type BankCallRequestInput = z.infer<typeof bankCallRequestInput>;
/** Add a Global Transfer client: an existing client (clientId) or a new one (clientName). */
export const globalTransferCreateInput = globalTransferDetailsInput
  .extend({ clientId: id.optional(), clientName: text(200).optional(), bankerId: optId })
  .strict()
  .refine((v) => !!v.clientId || !!v.clientName, { message: 'Pick a client or type a new client name', path: ['clientName'] });

// Bank applications (Credit Building: Capital One, Alliant, Navy Federal, Ramp, Amex Business Checking). The Access
// number is kept (the owner's decision: staff need it to call the bank representatives); online-banking passwords,
// PINs, security answers, full card/account numbers, full SSNs and full EINs are never accepted — unknown keys are
// rejected, cards/accounts are at most their last 4 digits and the EIN only its last 4.
const optDateOrBlank = dateOnly.nullish().or(z.literal(''));
const last4Input = (what: string) => z.string().trim().regex(/^\d{4}$/, `Exactly the last 4 digits — never the full ${what}`).nullish().or(z.literal(''));
export const bankRelationshipInput = z
  .object({
    accessNumber: z.string().trim().max(40).regex(/^[0-9A-Za-z -]*$/, 'Letters, digits, spaces and dashes only').nullish(),
    status: z.enum(BANK_RELATIONSHIP_STATUSES).optional(),
    memberSince: optDateOrBlank,
    eligibilityBasis: text(200).nullish(),
    businessName: text(200).nullish(),
    einLast4: last4Input('EIN'),
    legalName: text(200).nullish(),
    dateOfBirth: optDateOrBlank,
    phone: text(40).nullish(),
    email: z.string().trim().toLowerCase().email().max(200).nullish().or(z.literal('')),
    addressLine1: text(200).nullish(),
    addressLine2: text(200).nullish(),
    addressCity: text(120).nullish(),
    addressState: text(60).nullish(),
    addressZip: z.string().trim().regex(/^\d{5}(-\d{4})?$/, 'US ZIP code (12345 or 12345-6789)').nullish().or(z.literal('')),
    assigneeId: optId,
    notes: text(5000).nullish(),
  })
  .strict();
export type BankRelationshipInput = z.infer<typeof bankRelationshipInput>;
/** Add a client at a bank: an existing client (clientId) or a new one (clientName). */
export const bankRelationshipCreateInput = bankRelationshipInput
  .extend({ bank: z.enum(BANK_KEYS), clientId: id.optional(), clientName: text(200).optional() })
  .strict()
  .refine((v) => !!v.clientId || !!v.clientName, { message: 'Pick a client or type a new client name', path: ['clientName'] });

const bankUsd = z.coerce.number().min(0, 'Cannot be negative').max(10_000_000);
export const bankProductInput = z
  .object({
    productType: z.enum(BANK_PRODUCT_TYPES),
    productName: text(160).nullish(),
    applicationDate: optDateOrBlank,
    referenceNumber: text(80).nullish(),
    status: z.enum(BANK_PRODUCT_STATUSES).optional(),
    decisionDate: optDateOrBlank,
    limitAmountUsd: bankUsd.nullish(),
    securedAmountUsd: bankUsd.nullish(),
    aprPercent: z.coerce.number().min(0).max(100).nullish(),
    termMonths: z.coerce.number().int().min(1).max(600).nullish(),
    last4: last4Input('card or account number'),
    nextFollowUpDate: optDateOrBlank,
    notes: text(5000).nullish(),
  })
  .strict();
export type BankProductInput = z.infer<typeof bankProductInput>;
export const bankProductUpdateInput = bankProductInput.partial().strict();

// ID packs
export const idPackCreateInput = z.object({
  enrollmentId: id,
  bureaus: z.array(z.enum(CREDIT_BUREAUS)).min(1).max(3),
  documentIds: z.array(id).min(1).max(20),
  notes: text(2000).nullish(),
});
export const idPackAuthorizationInput = z.object({ method: z.enum(['portal', 'signed_form', 'email', 'other']), reference: text(300).nullish() });

// Operational payment requests (case manager → Jason or owner → Finance within 12 hours)
export const opsPaymentInput = z.object({
  clientId: optId,
  enrollmentId: optId,
  phaseNo: z.number().int().min(1).max(7).nullish(),
  expenseCategory: z.enum(OPS_EXPENSE_CATEGORIES),
  vendor: reqText(200),
  amountCents: z.number().int().positive().max(100_000_000),
  currency: z.string().trim().regex(/^[A-Za-z]{3}$/).transform((v) => v.toUpperCase()).default('USD'),
  purpose: reqText(2000),
  dueDate: dateOnly.nullish(),
  notes: text(2000).nullish(),
  /** Generated by the form; resubmitting the same request never creates a duplicate. */
  idempotencyKey: id,
  /** "Pay to": a saved payee (vendor). Paid by bank transfer through the payout engine unless payOutside is set. */
  payeeId: optId,
  /** "Send through": Airwallex (default) or Mercury (USD to US bank accounts, while Mercury is usable). */
  payoutProvider: z.enum(['airwallex', 'mercury']).nullish(),
  /** "Pay outside the portal": Finance pays manually and attaches the proof (e.g. IRS payments by check). */
  payOutside: z.boolean().optional(),
});
/** A saved payee (vendor) for operational payments; its bank details are entered with the bank-details form. */
export const opsPayeeInput = z.object({ name: reqText(200), notes: text(1000).nullish() });
export const opsPaymentReleaseInput = z.object({ fundingAccountId: z.string().trim().min(1).max(120), note: text(2000).nullish() });
export const opsPaymentDecisionInput = z.object({ decision: z.enum(['approve', 'reject']), note: text(2000).optional(), stepUpToken: z.string().min(20).max(200).optional() }).refine((v) => v.decision === 'approve' || !!v.note, { message: 'Give a reason', path: ['note'] });
export const opsPaymentPayInput = z.object({
  paidAt: dateTime,
  amountCents: z.number().int().positive().max(100_000_000),
  currency: z.string().trim().regex(/^[A-Za-z]{3}$/).transform((v) => v.toUpperCase()).default('USD'),
  method: reqText(120),
  transactionRef: reqText(200),
  note: text(2000).optional(),
});

// Client portal
export const clientInviteInput = z.object({ email: z.string().trim().toLowerCase().email() });
export const clientInquiryInput = z.object({ subject: reqText(200), message: reqText(5000), enrollmentId: optId });
